"use client";

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

// The cart is a shared tab: several staff can have the same booking open at
// once. Polling keeps every screen converged on the true server quantity, and
// when a poll reveals a change the local user didn't make, we toast them.
//
// A staff member's own edit also changes the cart data (optimistic update +
// refetch), so the mutations hook stamps each local change; data changes seen
// inside this window are treated as the user's own and don't toast.
const SELF_ECHO_WINDOW_MS = 3000;
// Collapse bursts (e.g. a multi-row change) into a single toast.
const TOAST_THROTTLE_MS = 2500;

const lastSelfActivityAt = new Map<string, number>();
const lastOtherToastAt = new Map<string, number>();

/** Called by the mutations hook whenever this client sends a cart change. */
export function markSelfCartActivity(bookingId: string) {
  lastSelfActivityAt.set(bookingId, Date.now());
}

// Fallback used when a shop item somehow lacks a vat_percentage. The actual
// rate comes from pricing config; this is just a safety net.
export const DEFAULT_VAT_PERCENTAGE = 25;

export interface BookingShopItem {
  id: number;
  shop_item_id: number;
  quantity: number;
  amount: number;
  total_price: number;
  total_price_excl_vat?: number;
  vat_amount?: number;
  total_price_incl_vat?: number;
  is_archived: boolean;
  shop_items: {
    id: number;
    name: string;
    image_url?: string | null;
    pricing: {
      price: number;
      vat_percentage: number;
    };
  };
}

export const barstaffCartQueryKey = (bookingId: string | null) =>
  ["barstaff", "cart", bookingId] as const;

async function fetchBarstaffCart(
  bookingId: string,
): Promise<BookingShopItem[]> {
  // Slim cart-only endpoint (just booking_shop_items) — keeps the 2s poll
  // cheap instead of re-running the heavy full-booking GET.
  const response = await fetch(`/api/bookings/${bookingId}/shop-items`);
  if (!response.ok) {
    let errorMsg = "Failed to fetch cart";
    try {
      const data = await response.json();
      errorMsg = data.error || errorMsg;
    } catch {}
    throw new Error(errorMsg);
  }
  const data = await response.json();

  const raw: BookingShopItem[] = data.data?.booking_shop_items || [];
  const activeItems = raw.filter((item) => !item.is_archived);

  // Always derive the monetary fields from amount × quantity rather than
  // trusting the server's total_price_excl_vat/vat_amount. Those columns are
  // populated on write, but if a read path ever omits them (or they come back
  // null) the cart would silently render 0.00 totals while the "each" price
  // still shows — masking real bar-shop revenue. Recomputing here keeps the
  // displayed total correct regardless of which columns the API selects.
  const computeRow = (
    item: BookingShopItem,
    totalQty: number,
  ): BookingShopItem => {
    const vatPct =
      item.shop_items?.pricing?.vat_percentage ?? DEFAULT_VAT_PERCENTAGE;
    const baseTotal = item.amount * totalQty;
    const vatAmount = Math.round(baseTotal * (vatPct / 100) * 100) / 100;
    return {
      ...item,
      quantity: totalQty,
      total_price: baseTotal,
      total_price_excl_vat: baseTotal,
      vat_amount: vatAmount,
      total_price_incl_vat: Math.round((baseTotal + vatAmount) * 100) / 100,
    };
  };

  // DB keeps one row per add for audit; UI groups by shop_item_id and sums.
  const grouped = activeItems.reduce((acc, item) => {
    const existing = acc.get(item.shop_item_id);
    if (!existing) {
      acc.set(item.shop_item_id, { item, totalQty: item.quantity });
      return acc;
    }

    const keepItem = existing.item.id > item.id ? existing.item : item;
    const totalQty = existing.totalQty + item.quantity;
    acc.set(item.shop_item_id, { item: keepItem, totalQty });
    return acc;
  }, new Map<number, { item: BookingShopItem; totalQty: number }>());

  return Array.from(grouped.values()).map((entry) =>
    computeRow(entry.item, entry.totalQty),
  );
}

export function useBarstaffCart(bookingId: string | null) {
  const query = useQuery({
    queryKey: barstaffCartQueryKey(bookingId),
    queryFn: () => fetchBarstaffCart(bookingId as string),
    enabled: !!bookingId,
    // Shared tab: poll so another staff member's changes appear here too.
    // react-query pauses this automatically while the tab is backgrounded.
    refetchInterval: 2000,
    refetchOnWindowFocus: true,
  });

  // When a poll (or refetch) reveals the cart changed and it wasn't this
  // user's own recent edit, let them know another staff member touched it.
  const prevSigRef = useRef<{ bookingId: string; sig: string } | null>(null);
  useEffect(() => {
    if (!bookingId || !query.data) return;

    const sig = query.data
      .map((i) => `${i.shop_item_id}:${i.quantity}`)
      .sort()
      .join("|");
    const prev = prevSigRef.current;
    prevSigRef.current = { bookingId, sig };

    // First load for this booking, or no change → nothing to announce.
    if (!prev || prev.bookingId !== bookingId || prev.sig === sig) return;

    // Suppress the user's own recent change, and throttle bursts.
    const lastSelf = lastSelfActivityAt.get(bookingId);
    if (lastSelf && Date.now() - lastSelf < SELF_ECHO_WINDOW_MS) return;
    const lastToast = lastOtherToastAt.get(bookingId);
    if (lastToast && Date.now() - lastToast < TOAST_THROTTLE_MS) return;
    lastOtherToastAt.set(bookingId, Date.now());

    toast.info("Another staff member updated this order");
  }, [query.data, bookingId]);

  const shopItems = query.data ?? [];
  const itemCount = shopItems.reduce((sum, item) => sum + item.quantity, 0);

  return {
    shopItems,
    isLoading: query.isPending && !!bookingId,
    error: query.error,
    itemCount,
  };
}
